import { useQuery } from "@apollo/client";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import CollapsibleSectionPanel from "components/CollapsibleSectionPanel";
import { configArrayNew } from "components/Environment/configArray";
import NotFoundPage from "components/error/NotFoundPage";
import FlashContext from "components/FlashMessages/FlashContext";
import { EmptystateVariablesColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import PageWrapper from "components/PageWrapper";
import PageInfo from "components/PageWrapper/Info";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { Stack } from "types/generated";
import { filterOutputReferences } from "utils/config";
import { AccountContext } from "views/AccountWrapper";

import StackHeader from "../components/Header";
import { StackContext } from "../Context";
import { getStacksBackUrl } from "../helpers";
import EnvVariableActionsCell from "./components/Cells/EnvVariableActionsCell";
import MountedFilesActionsCell from "./components/Cells/MountedFilesActionsCell";
import ContextsRow from "./components/ContextsRow";
import { showEnvVariableDrawer } from "./components/DrawerEnvVariable";
import { showMountedFilesFormDrawer } from "./components/DrawerMountedFile";
import EnvironmentTable from "./components/EnvironmentTable";
import FilesRow from "./components/FilesRow";
import VariablesRow from "./components/VariablesRow";
import {
  CONTEXTS_COLUMNS,
  CUSTOM_END_COLUMN_CONFIG,
  ENV_VARS_COLUMNS,
  EnvVariablesColumnId,
  MOUNTED_FILES_COLUMNS,
  SPACELIFT_VARS_COLUMNS,
} from "./constants";
import { GET_ENVIRONMENT } from "./gql";

const EnvironmentNew = () => {
  const { onError } = useTypedContext(FlashContext);
  const { stack: stackCtx } = useTypedContext(StackContext);
  const { viewer } = useTypedContext(AccountContext);

  useTitle(`Environment · ${stackCtx.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: stackCtx.name,
    },
  ]);

  const { error, loading, data } = useQuery<{ stack: Stack }>(GET_ENVIRONMENT, {
    onError,
    variables: { stackId: stackCtx.id },
  });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.stack) {
    return <PageLoading />;
  }

  if (!data?.stack) {
    return <NotFoundPage />;
  }

  const stack = { ...stackCtx, ...data?.stack };

  const [filteredRuntimeConfigElements] = filterOutputReferences(stack.runtimeConfig);
  const [localVariables, spaceliftVariables, mountedFiles] = configArrayNew(
    filteredRuntimeConfigElements
  );

  return (
    <>
      <StackHeader />
      <PageInfo title="Environment"></PageInfo>
      <PageWrapper margin="0" padding="0" gap="x-large">
        <CollapsibleSectionPanel
          ariaLevel={3}
          title="Environment Variables"
          initialIsCollapsed={false}
          count={localVariables.length}
          actions={
            <Button
              variant="secondary"
              onPress={() => showEnvVariableDrawer({ stackId: stackCtx.id, type: "STACK" })}
            >
              Add Variable
            </Button>
          }
          contentPadding="0"
        >
          <EnvironmentTable
            columnsConfig={ENV_VARS_COLUMNS}
            endCustomColumns={viewer.admin ? CUSTOM_END_COLUMN_CONFIG : null}
            emptyTable={
              <EmptyState
                padding="x-large 0"
                icon={EmptystateVariablesColored}
                title="You do not have any variables yet"
                caption="Environment variables are represented as a key-value mapping available to all processes running in a given environment. Click on the button above to create a new variable."
              />
            }
            items={localVariables}
            rowRenderer={(item, key) => (
              <VariablesRow
                key={key}
                config={item}
                isAdmin={viewer.admin}
                actionsComponent={EnvVariableActionsCell}
              />
            )}
            rowHeaderId={EnvVariablesColumnId.CREATED_AT}
            tableId="stack-env-variables"
          />
        </CollapsibleSectionPanel>
        <CollapsibleSectionPanel
          ariaLevel={3}
          title="Mounted files"
          count={mountedFiles.length}
          actions={
            <Button
              variant="secondary"
              onPress={() =>
                showMountedFilesFormDrawer({
                  stackId: stackCtx.id,
                  type: "STACK",
                })
              }
            >
              Add file
            </Button>
          }
          contentPadding="0"
        >
          <EnvironmentTable
            columnsConfig={MOUNTED_FILES_COLUMNS}
            endCustomColumns={viewer.admin ? CUSTOM_END_COLUMN_CONFIG : null}
            emptyTable={
              <EmptyState
                padding="x-large 0"
                icon={EmptystateVariablesColored}
                title="There are no mounted files for this environment"
                caption="Mounted files are represented as a key-value mapping available to all processes running in a given environment."
              />
            }
            items={mountedFiles}
            rowRenderer={(item, key) => (
              <FilesRow
                key={key}
                config={item}
                isAdmin={viewer.admin}
                actionsComponent={MountedFilesActionsCell}
              />
            )}
            rowHeaderId={EnvVariablesColumnId.CREATED_AT}
            tableId="stack-mounted-files"
          />
        </CollapsibleSectionPanel>
        <CollapsibleSectionPanel
          ariaLevel={3}
          title="Spacelift environment"
          count={spaceliftVariables.length}
          contentPadding="0"
        >
          <EnvironmentTable
            columnsConfig={SPACELIFT_VARS_COLUMNS}
            emptyTable={
              <EmptyState
                padding="x-large 0"
                icon={EmptystateVariablesColored}
                title="There are no Spacelift variables for this environment"
                caption="Environment variables are represented as a key-value mapping available to all processes running in a given environment."
              />
            }
            items={spaceliftVariables}
            rowRenderer={(item, key) => (
              <VariablesRow
                key={key}
                config={item}
                isAdmin={viewer.admin}
                actionsComponent={() => undefined}
              />
            )}
            rowHeaderId={EnvVariablesColumnId.NAME}
            tableId="stack-spacelift-env-variables"
          />
        </CollapsibleSectionPanel>

        {stack.attachedContexts.length ? (
          <CollapsibleSectionPanel
            ariaLevel={3}
            title="Attached contexts"
            count={stack.attachedContexts.length}
            contentPadding="0"
          >
            <EnvironmentTable
              columnsConfig={CONTEXTS_COLUMNS}
              items={stack.attachedContexts}
              rowRenderer={(item, key) => (
                <ContextsRow key={key} context={item} isAdmin={viewer.admin} />
              )}
              rowHeaderId={EnvVariablesColumnId.NAME}
              tableId="stack-attached-contexts"
            />
          </CollapsibleSectionPanel>
        ) : null}
      </PageWrapper>
    </>
  );
};

export default EnvironmentNew;
